import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import ReactDOMServer from 'react-dom/server';
import {
  AppCard,
  AppDatePicker,
  AppHelpModal,
  AppInput,
  AppInputNumber,
  AppNoInfo,
  AppRemixIcon,
  AppSingleSelect,
} from '@/layout/AppComponents';
import useIsMounted from '@/layout/components/hooks/useIsMounted';
import * as currentPageAction from '@/store/actions/currentPageAction';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath, path } from '@/routing/helpers/router';
import Translator from '@/layout/components/helpers/Translator';
import BreadcrumbHandler from '@/layout/components/breadcrumb/BreadcrumbHandler';
import AppTextEditor from '@/layout/components/form/Texts/AppTextEditor';
import DevelopmentCard from '@/layout/screenSpecifics/Modules/DynamicForm/DevelopmentCard';
import ModalWarning from '@/layout/screenSpecifics/Modules/DynamicForm/ModalWarning';

export default function ModuleDynamicFormStepScreen() {
  const { moduleBridgeHex, dynamicFormUserFormPageID } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector((s) => s?.module?.[moduleBridgeHex]?.moduleData);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState();
  const [modalWarning, setModalWarning] = useState();

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > LOAD FORM DATA
  const loadFormData = useCallback(
    async () =>
      axios
        .post(axiosPath('api_r_user_module_dynamicForm_step_get'), {
          moduleBridgeHex,
          dynamicFormUserFormPageID,
        })
        .then((r) => {
          if (isMounted.current) {
            if (r.status === 203) {
              setModalWarning(r?.data);
              return;
            }
            setFormData(r?.data);
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setLoading(false)),
    [moduleBridgeHex, dynamicFormUserFormPageID],
  );

  useEffect(() => {
    loadFormData();
  }, [dynamicFormUserFormPageID]);
  // < LOAD FORM DATA

  // > SAVE
  const handleSave = useCallback(
    async (values) =>
      axios
        .post(
          axiosPath(
            values?.back
              ? 'api_r_user_module_dynamicForm_back'
              : 'api_r_user_module_dynamicForm_step_edit',
          ),
          {
            moduleBridgeHex,
            dynamicFormUserFormPageID,
            dynamicFormUserFormID: formData?.dynamicFormUserFormID,
            ...values,
          },
        )
        .then((r) => {
          if (isMounted.current) {
            if (r?.data?.endConfirm || r?.data?.step) {
              navigate(
                path({
                  name: r?.data?.endConfirm
                    ? 'user_module_dynamicForm_endConfirm'
                    : 'user_module_dynamicForm_step',
                  params: {
                    moduleBridgeHex,
                    dynamicFormUserFormPageID: r?.data?.dynamicFormUserFormPageID,
                  },
                }),
              );
            } else if (r.status === 203) {
              setModalWarning(r?.data);
            }
          }
        })
        .catch((e) => dispatch(errorHandler(e)))
        .finally(() => setLoading(false)),
    [moduleBridgeHex, dynamicFormUserFormPageID, formData],
  );
  // < SAVE

  const downloadLawFile = useCallback(
    async (fileID) =>
      axios
        .post(axiosPath('api_r_user_module_dynamicForm_lawFile_get'), {
          moduleBridgeHex,
          fileID,
        })
        .then((r) => {
          if (isMounted.current) {
            window.open(r.data, '_blank');
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex],
  );

  const getDefaultValue = useCallback(
    (element) => {
      if (
        formData?.stepElements !== null &&
        element?.dynamicFormGlobalData !== null
      ) {
        return formData?.stepElements?.[element?.dynamicFormGlobalData?.code];
      }

      if (element?.defaultValue !== null && element?.defaultValue !== '') {
        return element?.defaultValue;
      }

      if (
        element?.dynamicFormGlobalData !== null &&
        formData?.pvGlobalData?.[element?.dynamicFormGlobalData?.code] !== null
      ) {
        return formData?.pvGlobalData?.[element?.dynamicFormGlobalData?.code];
      }

      if (element?.dynamicFormGlobalData?.defaultValue !== null) {
        return element?.dynamicFormGlobalData?.defaultValue;
      }

      return null;
    },
    [formData],
  );

  const getInitialValues = useCallback(() => {
    return (
      formData?.dynamicFormPageElements?.reduce((acc, element) => {
        if (element?.dynamicFormGlobalData?.type) {
          let value;

          switch (element?.dynamicFormGlobalData?.type) {
            case 1:
            case 2:
            case 3:
              value = getDefaultValue(element) || '';
              break;
            case 4:
              value = '';
              break;
            case 5:
              value = ''; // d/m/Y
              break;
            case 6:
              value = parseInt(getDefaultValue(element)) || null;
              break;
            default:
              value = '';
          }

          acc[element?.dynamicFormGlobalData?.code] = value;
        }

        return acc;
      }, {}) || {}
    );
  }, [formData]);

  const getValidationSchema = useCallback(() => {
    return Yup.object().shape(
      formData?.dynamicFormPageElements?.reduce((acc, element) => {
        if (element?.dynamicFormGlobalData?.type) {
          let value;

          switch (element?.dynamicFormGlobalData?.type) {
            case 1:
            case 2:
              value = Yup.number().required(
                ReactDOMServer.renderToString(
                  <Translator file="user" name="user.required" />,
                ),
              );
              break;
            case 3:
            case 4:
            case 5:
              value = Yup.string().required(
                ReactDOMServer.renderToString(
                  <Translator file="user" name="user.required" />,
                ),
              );
              break;
            case 6:
              value = Yup.number()
                .nullable()
                .required(
                  ReactDOMServer.renderToString(
                    <Translator file="user" name="user.required" />,
                  ),
                );
              break;
            default:
              value = '';
          }

          acc[element?.dynamicFormGlobalData?.code] = value;
        }

        return acc;
      }, {}) || {},
    );
  }, [formData]);

  return (
    <>
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: moduleData?.moduleName,
                  route: {
                    name: 'user_module_dynamicForm_home',
                    params: {
                      moduleBridgeHex,
                    },
                  },
                },
              ]
            : [{ name: '...' }]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          {loading ? (
            <AppNoInfo minHeight={window.innerHeight - 60} />
          ) : (
            <>
              {formData && (
                <>
                  <AppCard>
                    <Formik
                      initialValues={getInitialValues()}
                      validationSchema={getValidationSchema()}
                      validateOnChange={false}
                      validateOnBlur={false}
                      onSubmit={(v) => handleSave(v)}
                    >
                      {({
                        setFieldValue,
                        handleSubmit,
                        values,
                        errors,
                        isSubmitting,
                      }) => (
                        <form className="page-form" id="step" onSubmit={handleSubmit}>
                          <div className="container">
                            <div className="row">
                              <div className="col-lg-12">
                                {formData?.dynamicFormPageElements?.map((element) => (
                                  <div key={element?.id}>
                                    {element?.lawFiles?.length > 0 && (
                                      <div>
                                        {element?.lawFiles?.length === 1 ? (
                                          <button
                                            type="button"
                                            className="btn btn-danger btn-xs float-right"
                                            onClick={() =>
                                              downloadLawFile(
                                                element?.lawFiles?.[0]?.id,
                                              )
                                            }
                                          >
                                            <AppRemixIcon icon="file-pdf-2" />
                                          </button>
                                        ) : (
                                          <></>
                                          // <a
                                          //   className="element-lawFiles text-danger"
                                          //   data-toggle="popover"
                                          //   data-placement="left"
                                          //   title="Seleccionar"
                                          //   data-content="{% for file in element.lawFiles %}<a target='_blank' href='{{ path('module_user_dynamicform_get_law_file',{'fileId':file.id}) }}'><i class='text-danger fa fa-file-pdf-o'></i> {{ file.name }}</a><br>{% endfor %}"
                                          // >
                                          //   <i class="fa fa-file-pdf-o"></i>
                                          // </a>
                                        )}
                                        <div className="clearfix" />
                                      </div>
                                    )}
                                    <ElementContainer>
                                      {element?.dynamicFormGlobalData === null && (
                                        <div
                                          className="content-block"
                                          dangerouslySetInnerHTML={{
                                            __html: element?.defaultValue,
                                          }}
                                        />
                                      )}
                                      {element?.dynamicFormGlobalData?.type === 1 && (
                                        <div className="input">
                                          <AppInputNumber
                                            title={
                                              element?.dynamicFormGlobalData?.name
                                            }
                                            onChange={(v) =>
                                              setFieldValue(
                                                element?.dynamicFormGlobalData?.code,
                                                v,
                                              )
                                            }
                                            value={
                                              values?.[
                                                element?.dynamicFormGlobalData?.code
                                              ]
                                            }
                                            error={
                                              errors?.[
                                                element?.dynamicFormGlobalData?.code
                                              ]
                                            }
                                            isClearable
                                            required
                                          />
                                        </div>
                                      )}
                                      {element?.dynamicFormGlobalData?.type === 2 && (
                                        <div className="input">
                                          <AppInputNumber
                                            title={
                                              element?.dynamicFormGlobalData?.name
                                            }
                                            onChange={(v) =>
                                              setFieldValue(
                                                element?.dynamicFormGlobalData?.code,
                                                v,
                                              )
                                            }
                                            value={
                                              values?.[
                                                element?.dynamicFormGlobalData?.code
                                              ]
                                            }
                                            error={
                                              errors?.[
                                                element?.dynamicFormGlobalData?.code
                                              ]
                                            }
                                            isClearable
                                            required
                                          />
                                        </div>
                                      )}
                                      {element?.dynamicFormGlobalData?.type === 3 && (
                                        <div className="input">
                                          <AppInput
                                            title={
                                              element?.dynamicFormGlobalData?.name
                                            }
                                            onChange={(v) =>
                                              setFieldValue(
                                                element?.dynamicFormGlobalData?.code,
                                                v,
                                              )
                                            }
                                            value={
                                              values?.[
                                                element?.dynamicFormGlobalData?.code
                                              ]
                                            }
                                            error={
                                              errors?.[
                                                element?.dynamicFormGlobalData?.code
                                              ]
                                            }
                                            isClearable
                                            required
                                          />
                                        </div>
                                      )}
                                      {element?.dynamicFormGlobalData?.type === 4 && (
                                        <div className="input">
                                          <p>
                                            {element?.dynamicFormGlobalData?.name}
                                          </p>
                                          <AppTextEditor
                                            onChange={(v) =>
                                              setFieldValue(
                                                element?.dynamicFormGlobalData?.code,
                                                v,
                                              )
                                            }
                                            className="mb-2"
                                            value={
                                              values?.[
                                                element?.dynamicFormGlobalData?.code
                                              ]
                                            }
                                            error={
                                              errors?.[
                                                element?.dynamicFormGlobalData?.code
                                              ]
                                            }
                                          />
                                        </div>
                                      )}
                                      {element?.dynamicFormGlobalData?.type === 5 && (
                                        <div className="input">
                                          <AppDatePicker
                                            title={
                                              element?.dynamicFormGlobalData?.name
                                            }
                                            value={
                                              values?.[
                                                element?.dynamicFormGlobalData?.code
                                              ]
                                            }
                                            error={
                                              errors?.[
                                                element?.dynamicFormGlobalData?.code
                                              ]
                                            }
                                            onChange={(v) =>
                                              setFieldValue(
                                                element?.dynamicFormGlobalData?.code,
                                                v,
                                              )
                                            }
                                            isClearable
                                            showYearDropdown
                                            showMonthDropdown
                                          />
                                        </div>
                                      )}
                                      {element?.dynamicFormGlobalData?.type === 6 && (
                                        <div className="input">
                                          <AppSingleSelect
                                            options={
                                              element?.dynamicFormGlobalData
                                                ?.dynamicFormGlobalDataOptions
                                            }
                                            title={
                                              element?.dynamicFormGlobalData?.name
                                            }
                                            value={
                                              values?.[
                                                element?.dynamicFormGlobalData?.code
                                              ]
                                            }
                                            error={
                                              errors?.[
                                                element?.dynamicFormGlobalData?.code
                                              ]
                                            }
                                            onChange={(v) =>
                                              setFieldValue(
                                                element?.dynamicFormGlobalData?.code,
                                                v,
                                              )
                                            }
                                          />
                                        </div>
                                      )}

                                      {element?.dynamicFormPageElementHelps?.length >
                                        0 && (
                                        <div className="ml-2">
                                          <AppHelpModal
                                            iconSize={16}
                                            iconFilled
                                            size="lg"
                                          >
                                            {element?.dynamicFormPageElementHelps?.map(
                                              (dfpeh) => (
                                                <div
                                                  key={dfpeh?.id}
                                                  dangerouslySetInnerHTML={{
                                                    __html: dfpeh?.message,
                                                  }}
                                                />
                                              ),
                                            )}
                                          </AppHelpModal>
                                        </div>
                                      )}
                                    </ElementContainer>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="row mt-3">
                              <div className="col-lg-12 d-flex">
                                {!formData?.initPage && (
                                  <button
                                    type="button"
                                    className="btn btn-light"
                                    disabled={isSubmitting}
                                    onClick={async () => {
                                      handleSave({ ...values, back: true });
                                    }}
                                  >
                                    <AppRemixIcon
                                      icon="corner-down-left"
                                      className="mr-2"
                                    />
                                    Volver
                                  </button>
                                )}
                                <button
                                  type="submit"
                                  className="btn btn-success"
                                  disabled={isSubmitting}
                                  style={{ marginLeft: 'auto' }}
                                >
                                  Continuar <AppRemixIcon icon="arrow-right" filled />
                                </button>
                              </div>
                            </div>
                          </div>
                        </form>
                      )}
                    </Formik>
                  </AppCard>
                  <DevelopmentCard pvGlobalData={formData?.pvGlobalData} />
                </>
              )}
            </>
          )}
        </div>
      </main>
      <ModalWarning modalWarning={modalWarning} />
    </>
  );
}

const ElementContainer = styled.div`
  display: flex;
  align-items: center;
  .input,
  .content-block {
    flex-grow: 1;
  }
`;
